import { useCallback, useState } from 'react'
import { getUserEmailUniqueness, updateUser } from '@user/store/users/api'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { ITenantUserUpdate } from '@user/store/users/typings'
import { toast } from '@sales-center/components'
import {
  guestRoleName,
  updateUserEmail,
  updateUserSuccess,
} from '../UpdateUser/constants'
import { getLicenseInfo } from '@user/store/licensing/api'
import { isAPIError } from '@user/store/services/crud'
import { FormikHelpers } from 'formik'
import { useSelector } from 'react-redux'
import { selectAllUsers, selectUsersPage } from '@user/store/users'
import { selectLicenseInformation } from '@user/store/licensing/slice'

export const useUpdateUser = (
  handleRefreshList?: () => void,
  resetFormOnSuccess?: (formikHelpers: FormikHelpers<any>) => void,
) => {
  const dispatch = useAppDispatch()
  const users = useSelector(selectAllUsers)
  const pageUsers = useSelector(selectUsersPage)
  const licenseInformation = useSelector(selectLicenseInformation)

  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'fail'>(
    'idle',
  )

  const getUsers = async () => {
    try {
      const result = await dispatch(getUserEmailUniqueness())

      if (getUserEmailUniqueness.fulfilled.match(result)) {
        setStatus('success')
      } else {
        setStatus('fail')
        toast.error(`Error loading user list`)
      }
    } catch (error) {
      setStatus('fail')
    }
  }

  const execute = useCallback(
    async (
      user: ITenantUserUpdate,
      _reloadUsers?: boolean,
      formikHelpers?: FormikHelpers<any>,
    ) => {
      if (user) {
        try {
          setStatus('pending')

          // initialRoleName drives license counts, so a miss here can hand
          // out a license; the Users grid row may only be in the page snapshot
          const existingUser =
            pageUsers.find((u) => u.id === user.id) ??
            users.find((u) => u.id === user.id)
          user.initialRoleName = existingUser?.roleName
          if (
            user.initialRoleName === guestRoleName &&
            user.role?.name !== guestRoleName &&
            licenseInformation &&
            licenseInformation.availableLicenses <= 0
          ) {
            setStatus('fail')
            toast.error(
              'No available licenses remaining. Cannot update this user.',
            )
            return
          }

          const result = await dispatch(updateUser(user))
          if (updateUser.fulfilled.match(result)) {
            setStatus('success')
            toast.success(updateUserSuccess)
            if (
              result.payload.disabledAssignmentRulesCount &&
              result.payload.disabledAssignmentRulesCount > 0
            )
              if (result.payload.disabledAssignmentRulesCount == 1) {
                toast.success(
                  `1 assignment rule targeted at the deactivated user has been disabled.`,
                )
              } else {
                toast.success(
                  `${result.payload.disabledAssignmentRulesCount} assignment rules targeted at the deactivated user have been disabled.`,
                )
              }
            if (handleRefreshList) handleRefreshList()
            if (user.emailUpdated) toast.success(updateUserEmail)
            dispatch(getLicenseInfo())
            getUsers()
            if (resetFormOnSuccess && formikHelpers) {
              resetFormOnSuccess(formikHelpers)
            }
          } else if (isAPIError(result.payload)) {
            setStatus('fail')
            toast.error(result.payload.Message)
          } else {
            setStatus('fail')
            toast.error(`Unable to update user. ${result.error.message}`)
          }
        } catch (error) {
          setStatus('fail')
          toast.error(error.Message)
        }
      }
    },
    [
      dispatch,
      getUsers,
      handleRefreshList,
      licenseInformation,
      pageUsers,
      resetFormOnSuccess,
      users,
    ],
  )

  return { status, execute }
}
